import { MouseEvent, useState, useCallback } from 'react';
import { 
  TableCell, 
  TableRow, 
  Button, 
  Stack,  
  Container,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {  
  MEDIA_RECORD_TYPE_MAP, 
  MediaBasicDataDto, 
  MediaCommerceDto 
} from '@sr/dto';
import { IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridLayout } from 'shared/ui/grid-layout';
import { useMediaCommerce } from './media-commerce.hook';
import { formatDate, getDayText } from 'utils/date-format-helpers';
import PriceSpan from 'shared/ui/price-span';
import { formatPersonPruning } from 'utils/person-format-helpers';
import AddIcon from '@mui/icons-material/Add';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { MediaCommerceFormDialog, useMediaCommerceDialog } from './media-commerce-dialog';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { mediaCommerceApi } from './api-slice';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { useSnack } from 'shared/ui/snack';
import { LinkToMedia } from 'shared/ui/links/link-to-media';

type Props = {
  mediaId: number;
  media?: MediaBasicDataDto;
};

export const MediaCommerce = ({ mediaId, media }: Props) => {
  const showConfirmDeleteDialog = useConfirmDialog();
  const { showSuccess, showError } = useSnack();
  const [removeMediaCommerce] = mediaCommerceApi.useDeleteMediaCommerceMutation();
  const [rewriteAllPrices, setRewriteAllPrices] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<MediaCommerceDto | null>(null);
  
  const dialog = useMediaCommerceDialog(mediaId);
  
  const handleEditClick = useCallback((target: MediaCommerceDto) => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
    
    if (rewriteAllPrices && target.mediaId !== mediaId) {
      const priceToEdit = {
        ...target,
        mediaId: mediaId,
        id: mediaId,
        isNew: true
      };
      dialog.openToEdit(priceToEdit);
    } else {
      dialog.openToEdit(target);
    }
  }, [dialog, mediaId, rewriteAllPrices]);
  
  const handleDeleteClick = useCallback(async (target: MediaCommerceDto) => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
    
    if (rewriteAllPrices && target.mediaId !== mediaId) {
      showSuccess('Унаследованная цена будет пропущена при создании собственных цен');
      return;
    }
    
    try {
      await showConfirmDeleteDialog({
        content: 'Удалить строку цен для СМИ?'
      });
      await removeMediaCommerce(target.id).unwrap();
      showSuccess('Строка успешно удалена');
    } catch (e) {
      showError(`Ошибка удаления строки: ${extractNestJsErrorMessage(e)}`);
    }
  }, [mediaId, rewriteAllPrices, removeMediaCommerce, showConfirmDeleteDialog, showError, showSuccess]);
  
  const handleMenuOpen = (e: MouseEvent<HTMLElement>, item: MediaCommerceDto) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedItem(item);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedItem(null);
  };
  
  const shouldHideDelete = selectedItem ? (rewriteAllPrices && selectedItem.mediaId !== mediaId) : false;
  
  const { collection, isLoading, error } = useMediaCommerce(mediaId);
  
  const parentMediaId = media?.parentMedia?.id;
  const { collection: parentCollection } = useMediaCommerce(parentMediaId || 0);
  
  const hasOwnPrices = collection && collection.length > 0;
  const showInheritedPrices = media?.parentMedia && !hasOwnPrices;
  
  let displayPrices: MediaCommerceDto[] = [];
  let isShowingInherited = false;
  
  if (showInheritedPrices && parentCollection) {
    if (!rewriteAllPrices) {
      displayPrices = parentCollection.map(item => ({
        ...item,
        isInherited: true
      }));
      isShowingInherited = true;
    } else {
      displayPrices = parentCollection.map(item => ({
        ...item,
        isInherited: true,
        canEdit: true
      }));
      isShowingInherited = false;
    }
  } else {
    displayPrices = collection || [];
    isShowingInherited = false;
  }

  const shouldShowEmptyStateWithButtons = !hasOwnPrices && !media?.parentMedia;

  if (error)
    return (
      <Container>
        <ErrorBanner errorMessage={'Ошибка при загрузке данных'} />
      </Container>
    );

  if (isLoading)
    return (
      <Container>
        <LoadingBanner />
      </Container>
    );
    
  return (
    <>
      {showInheritedPrices && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={rewriteAllPrices}
                onChange={(e) => setRewriteAllPrices(e.target.checked)}
                color="primary"
              />
            }
            label="Создать собственные цены"
          />
          {!rewriteAllPrices && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5 }}>
              Используются цены материнского СМИ (<LinkToMedia media={media.parentMedia}>
                {media?.parentMedia?.name}
              </LinkToMedia>)
            </Typography>
          )}
        </Box>
      )}
      
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 2, mb: 2 }}>
        <Button 
          size={'small'} 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={dialog.openToAdd}
          disabled={showInheritedPrices && !rewriteAllPrices}
          title={showInheritedPrices && !rewriteAllPrices ? 'Включите \'Создать собственные цены\' для добавления' : ''}
        >
          Добавить строку
        </Button>
      </Stack>
      
      <GridLayout<MediaCommerceDto>
        columns={['Наименование', 'Тип записи', 'Продолжительность', 'Цена', 'Цена производителя', '%', 'Обновил', 'Дата обновления', '']}
        items={displayPrices}
        isLoading={isLoading}
        error={error ? String(error) : undefined}
        itemRender={(item) => (
          <MediaCommerceRow
            onMenuClick={(e) => handleMenuOpen(e, item)}
            key={item.id}
            mediaCommerceData={item}
            isInherited={showInheritedPrices}
            isEditDisabled={showInheritedPrices && !rewriteAllPrices}
            isRewriteMode={rewriteAllPrices}
          />
        )} 
        noItemsText={'Список цен на СМИ пуст'} 
      />
      
      {showInheritedPrices && !rewriteAllPrices && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Все цены унаследованы. Включите "Создать собственные цены" для редактирования.
        </Typography>
      )}
      
      {showInheritedPrices && rewriteAllPrices && (
        <Typography variant="caption" color="info.main" sx={{ mt: 1, display: 'block' }}>
          Редактируйте унаследованные цены, чтобы создать собственные. Изменения сохранятся для текущего СМИ.
        </Typography>
      )}
      
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem 
          onClick={() => selectedItem && handleEditClick(selectedItem)}
          disabled={showInheritedPrices && !rewriteAllPrices}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {rewriteAllPrices && selectedItem?.mediaId !== mediaId ? 'Создать копию' : 'Редактировать'}
          </ListItemText>
        </MenuItem>
        
        {!shouldHideDelete && (
          <MenuItem 
            onClick={() => selectedItem && handleDeleteClick(selectedItem)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Удалить</ListItemText>
          </MenuItem>
        )}
      </Menu>
      
      <MediaCommerceFormDialog {...dialog.dialogProps} />
    </>
  );
};

type RowProps = {
  mediaCommerceData: MediaCommerceDto,
  onMenuClick: (e: MouseEvent<HTMLElement>) => void;
  isInherited?: boolean;
  isEditDisabled?: boolean;
  isRewriteMode?: boolean;
};

const MediaCommerceRow = (props: RowProps) => {
  const { 
    mediaCommerceData, 
    onMenuClick, 
    isInherited = false,
    isEditDisabled = false,
    isRewriteMode = false
  } = props;

  const textColor = isInherited && !isRewriteMode ? 'text.secondary' : 'text.primary';
  const isReadOnly = isInherited && !isRewriteMode;

  return (
    <TableRow key={mediaCommerceData.id}>
      <TableCell>
        <Typography 
          variant="body2" 
          color={textColor}
        >
          {mediaCommerceData.name}
          {isInherited && !isRewriteMode && ' (унасл.)'}
          {isInherited && isRewriteMode && ' (копировать)'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          {MEDIA_RECORD_TYPE_MAP[mediaCommerceData.typeRecord]}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          {`${mediaCommerceData.durationDays} ${getDayText(Number(mediaCommerceData.durationDays))}`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          <PriceSpan price={Number(mediaCommerceData.price)} />
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          <PriceSpan price={Number(mediaCommerceData.productionPrice)} />
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          {mediaCommerceData.percentages}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          {formatPersonPruning(mediaCommerceData.user)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color={textColor}>
          {formatDate(mediaCommerceData.updatedAt)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton 
          edge="end" 
          onClick={onMenuClick}
          disabled={isReadOnly}
          title={isReadOnly ? 'Включите \'Создать собственные цены\' для редактирования' : ''}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

MediaCommerce.Row = MediaCommerceRow;